/* eslint-disable ts/strict-boolean-expressions */
import { Button, Dialog, DialogContent, DialogFooter, FormInput, Heading, ScrollArea, Text } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUIStateStore } from "@/store/uiStateStore";

import { apiCall } from "../api/shared";
import useAppStore from "../store/appStateZustand";
// @ts-expect-error - LoadingSmall is a JSX component without TypeScript declarations
import LoadingSmall from "./LoadingSmall";

// Define our own types locally to avoid import issues
type Account = {
  artistaccountcode: string;
  accountname: string;
  cataloguecode: string;
  [key: string]: unknown;
};

type SongSuggestionsModalProps = {
  open: boolean;
  onClose: () => void;
  accounts?: Account[];
  totalSuggestionsCount?: number;
};

type SpotifyCountResponse = Array<{
  count: number; // The API returns a number, not a string
  [key: string]: unknown;
}>;

type AccountCounts = {
  [key: string]: number;
};

// Type guard for Spotify count response
function isSpotifyCountResponse(data: unknown): data is SpotifyCountResponse {
  return Array.isArray(data)
    && data.length > 0
    && typeof data[0] === "object"
    && data[0] !== null
    && "count" in data[0]
    && typeof (data[0] as Record<string, unknown>).count === "number"; // Changed from string to number
}

function SongSuggestionsModal({ open, onClose, accounts, totalSuggestionsCount }: SongSuggestionsModalProps) {
  const { t } = useTranslation("dashboard");
  const flags = useFlags();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const navigate = useNavigate();
  const setSelectedAccountInStore = useAppStore(s => s.setSelectedAccount);
  const setSelectedCatalogueInStore = useAppStore(s => s.setSelectedCatalogue);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [accountCounts, setAccountCounts] = useState<AccountCounts>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState<boolean>(false);
  const [loadedAccountsCount, setLoadedAccountsCount] = useState<number>(0);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadingAccountIds, setLoadingAccountIds] = useState<Set<string>>(() => new Set());

  const ACCOUNTS_PER_LOAD = 10; // Load 10 accounts at a time

  // Fetch counts for a batch of accounts
  const fetchAccountsBatch = async (accountsToFetch: Account[]) => {
    const counts: AccountCounts = {};

    // Process accounts in smaller batches to avoid overwhelming the API
    const batchSize = 3; // Even smaller batches to be more reliable
    for (let i = 0; i < accountsToFetch.length; i += batchSize) {
      const batch = accountsToFetch.slice(i, i + batchSize);

      const batchPromises = batch.map(async (account) => {
        try {
          const data: unknown = await apiCall({
            endpoint: `/top-spotify?acs=${account.artistaccountcode}`,
          });

          const count = isSpotifyCountResponse(data) ? data[0].count : 0;
          return { accountCode: account.artistaccountcode, count };
        }
        catch (error) {
          console.error(`Failed to fetch count for account ${account.artistaccountcode}:`, error);
          return { accountCode: account.artistaccountcode, count: 0 };
        }
      });

      // Wait for this batch to complete
      const batchResults = await Promise.allSettled(batchPromises);

      // Process results
      batchResults.forEach((result) => {
        if (result.status === "fulfilled") {
          counts[result.value.accountCode] = result.value.count;
        }
      });

      // Add a delay between batches to be nice to the API
      if (i + batchSize < accountsToFetch.length) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    return counts;
  };

  // Initial load when modal opens - only load first batch
  useEffect(() => {
    if (open && accounts && accounts.length > 0) {
      const fetchInitialCounts = async () => {
        setIsLoadingCounts(true);
        setAccountCounts({}); // Clear previous counts
        setLoadedAccountsCount(0); // Reset loaded count

        const initialBatch = accounts.slice(0, ACCOUNTS_PER_LOAD);
        const counts = await fetchAccountsBatch(initialBatch);

        setAccountCounts(counts);
        setLoadedAccountsCount(initialBatch.length);
        setIsLoadingCounts(false);
      };

      void fetchInitialCounts();
    }
  }, [open, accounts]);

  // Load more accounts function
  const handleLoadMore = async () => {
    if (!accounts || loadingMore || loadedAccountsCount >= accounts.length) {
      return;
    }

    setLoadingMore(true);

    const nextBatch = accounts.slice(
      loadedAccountsCount,
      loadedAccountsCount + ACCOUNTS_PER_LOAD,
    );

    const newCounts = await fetchAccountsBatch(nextBatch);

    setAccountCounts(prev => ({ ...prev, ...newCounts }));
    setLoadedAccountsCount(prev => prev + nextBatch.length);
    setLoadingMore(false);
  };

  // Fetch counts for searched accounts when user searches
  useEffect(() => {
    if (searchQuery.trim() && accounts) {
      const searchedAccounts = accounts.filter((account) => {
        const searchLower = searchQuery.toLowerCase();
        const nameMatch = account.accountname?.toLowerCase().includes(searchLower);
        const codeMatch = account.artistaccountcode?.toLowerCase().includes(searchLower);
        return nameMatch || codeMatch;
      });

      // Find accounts we need to fetch counts for
      const accountsNeedingCounts = searchedAccounts.filter(
        account => !(account.artistaccountcode in accountCounts),
      );

      // Fetch counts for those accounts
      if (accountsNeedingCounts.length > 0) {
        // Mark these accounts as loading
        setLoadingAccountIds((prev) => {
          const newSet = new Set(prev);
          accountsNeedingCounts.forEach(account => newSet.add(account.artistaccountcode));
          return newSet;
        });

        const fetchSearchCounts = async () => {
          const searchCounts = await fetchAccountsBatch(accountsNeedingCounts);
          setAccountCounts(prev => ({ ...prev, ...searchCounts }));

          // Remove from loading set
          setLoadingAccountIds((prev) => {
            const newSet = new Set(prev);
            accountsNeedingCounts.forEach(account => newSet.delete(account.artistaccountcode));
            return newSet;
          });
        };
        void fetchSearchCounts();
      }
    }
  }, [searchQuery, accounts, accountCounts]);

  // Filter accounts based on search and respect loaded accounts limit
  const filteredAccounts = useMemo(() => {
    if (!accounts)
      return [];

    // If searching, show all matching accounts
    if (searchQuery.trim()) {
      return accounts.filter((account) => {
        const searchLower = searchQuery.toLowerCase();
        const nameMatch = account.accountname?.toLowerCase().includes(searchLower);
        const codeMatch = account.artistaccountcode?.toLowerCase().includes(searchLower);
        return nameMatch || codeMatch;
      });
    }

    // If not searching, only show loaded accounts
    return accounts.slice(0, loadedAccountsCount);
  }, [accounts, searchQuery, loadedAccountsCount]);

  // Check if there are more accounts to load (only when not searching)
  const hasMoreAccounts = useMemo(() => {
    if (!accounts || searchQuery.trim())
      return false;
    return loadedAccountsCount < accounts.length;
  }, [accounts, loadedAccountsCount, searchQuery]);

  const handleAccountSelect = (account: Account) => {
    // Set the selected account in the store
    setSelectedAccountInStore(account.artistaccountcode);
    setSelectedCatalogueInStore(account.cataloguecode);

    // Navigate to songs page (which will now show the selected account's suggestions)
    void navigate({ to: "/songs" });

    // Close the modal
    onClose();
  };

  const handleViewAllSuggestions = () => {
    // Navigate to songs without changing account (shows aggregated view)
    void navigate({ to: "/songs" });
    onClose();
  };

  if (!accounts || accounts.length === 0) {
    return null;
  }

  if (!flags["pd-spotify-suggestions-feature"]) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <div className={`space-y-6 ${isTunecore ? "text-white" : ""}`}>
          <div className="space-y-4">
            <Heading as="h3" className={` ${isTunecore ? "text-white" : ""}`}>
              {t("dashboard:song-suggestions-by-account", "Song Suggestions by Account")}
            </Heading>
            <Text as="p">
              {t("dashboard:song-suggestions-description", "Choose an account to view its specific song suggestions, or view all suggestions across accounts.")}
            </Text>
          </div>

          <FormInput
            label={String(t("dashboard:search-accounts", "Search Accounts"))}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={String(t("dashboard:search-accounts-placeholder", "Search by account name or code..."))}
          />

          {/* Summary */}
          <div className="flex items-center justify-between p-3 bg-gray-200 rounded-lg bg-opacity-20">
            <Text as="p" className="font-medium">
              {t("dashboard:total-suggestions-all-accounts", "Total Suggestions (All Accounts)")}
            </Text>
            <Text as="p" className="text-xl font-bold">
              {totalSuggestionsCount?.toLocaleString() ?? "-"}
            </Text>
          </div>

          {/* Accounts List */}
          <ScrollArea className={`h-[400px] border rounded-lg ${isTunecore ? "border-tunecore-light-grey" : "border-sentric-mid-grey"}`}>
            <div className="p-4 space-y-3">
              {isLoadingCounts ? (
                <div className="py-8 text-center">
                  <Text as="p" className={isTunecore ? "text-white" : "text-black"}>
                    {t("dashboard:loading-suggestions", "Loading suggestion counts...")}
                  </Text>
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="py-8 text-center">
                  <Text as="p" className={isTunecore ? "text-white" : "text-black"}>
                    {t("dashboard:no-accounts-found", "No accounts found")}
                  </Text>
                </div>
              ) : (
                filteredAccounts.map((account) => {
                  const suggestionCount = accountCounts[account.artistaccountcode] || 0;
                  const isAccountLoading = loadingAccountIds.has(account.artistaccountcode);

                  return (
                    <button
                      key={account.artistaccountcode}
                      type="button"
                      className={`w-full p-4 border rounded-lg cursor-pointer transition-colors text-left ${
                        isTunecore
                          ? "bg-[#282828] border-tunecore-light-grey hover:bg-tunecore-light-grey"
                          : "bg-white border-sentric-mid-grey hover:bg-primary-button-hover"
                      }`}
                      onClick={() => handleAccountSelect(account)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <Text as="p" className={`font-semibold ${isTunecore ? "text-white" : ""}`}>
                            {account.accountname}
                          </Text>
                          <Text as="p" className={`text-sm opacity-75 ${isTunecore ? "text-white" : ""}`}>
                            {account.artistaccountcode}
                          </Text>
                        </div>
                        <div className="text-right">
                          {isAccountLoading ? (
                            <div className="flex items-center justify-center h-8">
                              <LoadingSmall />
                            </div>
                          ) : (
                            <>
                              <Text as="p" className={`text-2xl font-bold ${isTunecore ? "text-white" : ""}`}>
                                {suggestionCount.toLocaleString()}
                              </Text>
                              <Text as="p" className={`text-xs opacity-75 ${isTunecore ? "text-white" : ""}`}>
                                {t("dashboard:suggestions", "suggestions")}
                              </Text>
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}

              {/* Load More Button */}
              {hasMoreAccounts && !isLoadingCounts && (
                <div className="pt-4 text-center border-t border-opacity-20">
                  <Button
                    variant="outline"
                    label={loadingMore
                      ? t("dashboard:loading-more", "Loading more...")
                      : t("dashboard:load-more-accounts", `Load More`)}
                    onClick={() => void handleLoadMore()}
                    disabled={loadingMore}
                    className={`w-full ${isTunecore ? "border-tunecore-light-grey text-white hover:bg-tunecore-light-grey" : ""}`}
                  />
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button
              variant="outline"
              label={t("dashboard:cancel", "Cancel")}
              onClick={onClose}
              className={isTunecore ? "border-tunecore-light-grey text-white hover:bg-tunecore-light-grey" : ""}
            />
            <Button
              variant={isTunecore ? "primary" : "secondary"}
              label={t("dashboard:view-all-suggestions", "View All Suggestions")}
              onClick={handleViewAllSuggestions}
            />
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SongSuggestionsModal;
